import { ReactElement, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../../../contexts/ConfigContext";
import { SliderQuestionType } from "../../../../types/Question";
import RangeInput from "../../RangeInput";
import styled from "styled-components";

interface AnswerSliderProps {
  question: SliderQuestionType;
  value?: number | null;
  onChange: (value: number | null) => void;
}

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;

const isNumberish = (v: unknown) => typeof v === "number" && Number.isFinite(v);

const AnswerSlider = ({
  question,
  value,
  onChange,
}: AnswerSliderProps): ReactElement => {
  const {
    detail: { step, answers, tick_interval, minor_tick_interval, prefix, suffix },
  } = question;

  const { t } = useTranslation();
  const { selectedRole } = useConfig();

  const [min, max] = useMemo(() => {
    if (answers.length < 2) return [DEFAULT_MIN, DEFAULT_MAX];
    const values = answers.map((a) => a.value);
    return [Math.min(...values), Math.max(...values)];
  }, [answers]);

  const answered = isNumberish(value);

  const [hintActive, setHintActive] = useState(!answered);
  useEffect(() => {
    if (answered) setHintActive(false);
  }, [answered]);

  const [assetActivated, setAssetActivated] = useState<boolean>(answered);
  useEffect(() => {
    if (answered) setAssetActivated(true);
  }, [answered]);

  const [currentAsset, setCurrentAsset] = useState<string | null>(null);
  const [prevAsset, setPrevAsset] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  // ✓ on handle
  const [savedFlash, setSavedFlash] = useState(false);
  // 🎉 confetti fire token
  // const [confettiFlash, setConfettiFlash] = useState(false);

  // SR live region
  const [srMessage, setSrMessage] = useState("");

  // On mount (or when an answered value arrives), show ✓ but NO confetti
  useEffect(() => {
    if (answered) {
      setHintActive(false);
      setAssetActivated(true);
      setSavedFlash(true);
      const t = setTimeout(() => setSavedFlash(false), 1);
      return () => clearTimeout(t);
    }
  }, [answered]);

  const sorted = useMemo(
    () => [...answers].sort((a, b) => a.value - b.value),
    [answers]
  );

  const decimals = useMemo(() => {
    const s = String(step ?? 1);
    const idx = s.indexOf(".");
    return idx >= 0 ? s.length - idx - 1 : 0;
  }, [step]);

  const snapToStep = (n: number) => {
    const s = step && step > 0 ? step : 1;
    const snapped = Math.round((n - min) / s) * s + min;
    const clamped = Math.min(max, Math.max(min, snapped));
    return Number(clamped.toFixed(decimals));
  };

  const pickNearestAsset = (target: number) => {
    let best: (typeof answers)[number] | null = null;
    let bestDiff = Infinity;
    for (const a of sorted) {
      const hasAsset = typeof a.asset === "string" && a.asset.trim() !== "";
      if (!hasAsset) continue;
      const d = Math.abs(a.value - target);
      if (d < bestDiff) {
        best = a;
        bestDiff = d;
      }
    }
    return best;
  };

  useEffect(() => {
    if (!answers?.length) return;

    let candidate = null;

    if (!isNumberish(value)) {
      const middle = snapToStep(min + (max - min) / 2);
      candidate = pickNearestAsset(middle);
    } else {
      candidate = pickNearestAsset(value as number);
    }

    const newAsset = (candidate?.asset as string) || null;

    if (newAsset !== currentAsset) {
      setPrevAsset(currentAsset);
      setCurrentAsset(newAsset);
      setTransitioning(true);
      const timeout = setTimeout(() => {
        setPrevAsset(null);
        setTransitioning(false);
      }, 350);
      return () => clearTimeout(timeout);
    }
  }, [value, answers, min, max]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cache: HTMLImageElement[] = [];
    answers.forEach((a) => {
      if (typeof a.asset === "string" && a.asset.trim()) {
        const img = new Image();
        img.src = a.asset;
        cache.push(img);
      }
    });
  }, [answers]);

  const formatPin = (n: number) => {
    const num = Number.isFinite(n) ? Number(n.toFixed(decimals)) : n;
    const pre = typeof prefix === "string" ? prefix : "";
    const suf = typeof suffix === "string" ? suffix : "";
    return `${pre}${num}${suf}`;
  };

  const currentOpacity = assetActivated ? 1 : 0.5;
  const defaultCenter = snapToStep(min + (max - min) / 2);

  return (
    <Wrapper>
      <HiddenPreloadLayer>
        {answers.map(
          (a, i) =>
            typeof a.asset === "string" && (
              <img key={i} src={a.asset} alt="" loading="eager" />
            )
        )}
      </HiddenPreloadLayer>

      {(currentAsset || prevAsset) && (
        <AssetContainer>
          {prevAsset && transitioning && (
            <AssetImage
              src={prevAsset}
              alt="previous"
              style={{ opacity: 0 }}
              $baseOpacity={1}
            />
          )}
          {currentAsset && (
            <AssetImage
              key={currentAsset}
              src={currentAsset}
              alt="slider visual"
              $baseOpacity={currentOpacity}
            />
          )}
        </AssetContainer>
      )}

      <RangeInput
        min={min}
        max={max}
        step={step}
        formatPin={formatPin}
        tick={tick_interval}
        subTick={minor_tick_interval}
        value={[isNumberish(value) ? (value as number) : defaultCenter]}
        options={answers}
        isAnswered={answered}
        showHint={!assetActivated}
        ariaNoValueText={t("slider.noValueSelected", "No value selected. Slide to choose.")}
        savedFlash={savedFlash}         // ✓ on handle
        // confettiFlash={confettiFlash}   // 🎉 only when commit happens
        onFirstInteract={() => {
          setHintActive(false);
          setAssetActivated(true);
        }}
        onChange={(values) => {
          const newVal = Number(values[0].toFixed(decimals));

          // Optimistic success feedback:
          setSavedFlash(true);                 // ✓
          setTimeout(() => setSavedFlash(false), 1);

          // setConfettiFlash(true);              // 🎉
          // setTimeout(() => setConfettiFlash(false), 1);

          setSrMessage(t("slider.saved", "Answer saved."));
          setHintActive(false);
          setAssetActivated(true);
          onChange(newVal);
        }}
        onDrag={(values) => {
          const newVal = Number(values[0].toFixed(decimals));
          const cand = pickNearestAsset(newVal);
          const newAsset = (cand?.asset as string) || null;

          if (newAsset !== currentAsset) {
            requestAnimationFrame(() => {
              setPrevAsset(currentAsset);
              setCurrentAsset(newAsset);
              setTransitioning(true);
              setTimeout(() => {
                setPrevAsset(null);
                setTransitioning(false);
              }, 350);
            });
          }

          if (!assetActivated) setAssetActivated(true);
        }}
      />

      <SrOnly aria-live="polite" role="status">{srMessage}</SrOnly>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const HiddenPreloadLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;

const AssetContainer = styled.div`
  width: 100%;
  height: 110px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  position: relative;
`;

const AssetImage = styled.img<{ $baseOpacity?: number }>`
  position: absolute;
  max-width: 90px;
  max-height: 90px;
  width: 90px;
  object-fit: contain;
  border-radius: 10px;
  user-select: none;
  pointer-events: none;
  opacity: ${(p) => (p.$baseOpacity === undefined ? 1 : p.$baseOpacity)};
  transition: opacity 0.35s ease-in-out;
`;

const SrOnly = styled.div`
  border: 0 !important;
  clip: rect(0 0 0 0) !important;
  clip-path: inset(50%) !important;
  height: 1px !important;
  margin: -1px !important;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 1px !important;
`;

export default AnswerSlider;
